// PostHog product-analytics integration.
//
// Wired into the existing analytics choke point (ga4.js): every meaningful,
// PII-scrubbed event the app already fires is forwarded here, plus login-time
// identify / logout-time reset. This gives the visit->purchase funnel, product
// usage, and session replay without re-instrumenting any call sites.
//
// The project token is a PostHog WRITE-ONLY public key — safe to ship in the
// client bundle (it can send events, never read data). Overridable via env.
import posthog from 'posthog-js';

const POSTHOG_KEY = String(
  process.env.REACT_APP_POSTHOG_KEY || 'phc_ngrmMJ8W8chkfSPHR2iVUxXQoSivcypZc6bGnUjw2soS'
).trim();
const POSTHOG_HOST = String(
  process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com'
).trim();

let ready = false;

const hasWindow = () => typeof window !== 'undefined';

// PostHog exception autocapture sends raw error messages + stack traces. This
// app redacts PII from errors everywhere else (frontendErrorTracker.js), so we
// hold the same line here: scrub exception text before it leaves the browser.
const PII_PATTERNS = [
  [/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted_email]'],
  [/(?:\+?\d[\d\s\-().]{8,}\d)/g, '[redacted_phone]'],
  [/\b(?:\d[ -]*?){13,19}\b/g, '[redacted_card]'],
  [/\b(?:eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)\b/g, '[redacted_token]'],
  [/\b\d{7,}\b/g, '[redacted_number]'],
];

const scrubPII = (value) => {
  let text = String(value == null ? '' : value);
  for (const [pattern, replacement] of PII_PATTERNS) text = text.replace(pattern, replacement);
  return text;
};

// Runs on every outbound event; only rewrites exception text, passes the rest
// through untouched. Never throws.
const scrubExceptionEvent = (event) => {
  try {
    if (event && event.event === '$exception' && event.properties) {
      const p = event.properties;
      if (p.$exception_message) p.$exception_message = scrubPII(p.$exception_message);
      if (Array.isArray(p.$exception_list)) {
        p.$exception_list.forEach((ex) => { if (ex && ex.value) ex.value = scrubPII(ex.value); });
      }
    }
  } catch (e) {}
  return event;
};

export const initPostHog = () => {
  // Caller (ga4.initializeAnalytics) has already passed shouldTrack(), so this
  // only runs when analytics is permitted for this visitor/host.
  if (!hasWindow() || ready || !POSTHOG_KEY) return;
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Only build person profiles for logged-in users we identify — keeps
      // anonymous browsing lightweight and privacy-friendly.
      person_profiles: 'identified_only',
      // Pageviews are forwarded from the app's own router (see ga4.trackEvent)
      // so GA4 and PostHog count the exact same navigations.
      capture_pageview: false,
      autocapture: true,
      // Error Tracking: auto-capture uncaught errors + unhandled promise
      // rejections as proper $exception events (grouped issues + stack traces
      // in PostHog), with PII scrubbed on the way out.
      capture_exceptions: true,
      before_send: scrubExceptionEvent,
      session_recording: {
        // Never record form field VALUES: passwords, emails, names, and any
        // card-adjacent inputs are masked. Card numbers themselves live in
        // Stripe/PayPal cross-origin iframes, which replay cannot capture.
        maskAllInputs: true,
        // Opt-in hook: add data-ph-mask (or class "ph-mask") to any element
        // whose on-screen text should also be hidden from replays.
        maskTextSelector: '[data-ph-mask]',
      },
      loaded: () => { ready = true; },
    });
    ready = true;
  } catch (e) {
    // Analytics must never break the app.
  }
};

export const posthogCapture = (event, properties) => {
  if (!ready || !hasWindow()) return;
  try {
    posthog.capture(String(event || ''), properties || {});
  } catch (e) {}
};

// Tie behavior to a real account. Called on login with the numeric account id
// (never email) so the funnel and session replays attribute to the same person
// the server-side purchase events use.
export const posthogIdentify = (userId, properties) => {
  if (!ready || !hasWindow()) return;
  const id = String(userId || '').trim();
  if (!id || id.includes('@')) return;
  try {
    posthog.identify(id, properties || {});
  } catch (e) {}
};

export const posthogReset = () => {
  if (!ready || !hasWindow()) return;
  try {
    posthog.reset();
  } catch (e) {}
};

// React render errors are swallowed by the error boundary and never reach
// window.onerror, so autocapture misses them — capture them explicitly.
export const posthogCaptureException = (error, properties) => {
  if (!ready || !hasWindow()) return;
  try {
    posthog.captureException(error, properties || {});
  } catch (e) {}
};

// Hard stop for exempt visitors (owner IPs): kills autocapture/replay even if
// init already ran before the IP check answered. Safe no-op when never inited.
export const posthogOptOut = () => {
  if (!ready || !hasWindow()) return;
  try {
    posthog.opt_out_capturing();
  } catch (e) {}
};

export default {
  initPostHog,
  posthogCapture,
  posthogIdentify,
  posthogReset,
  posthogCaptureException,
  posthogOptOut,
};
